/**
 * Worker API wire shapes — the seam between the client sync queue
 * (dev-core-domain, src/data) and the Worker (dev-platform, src/worker).
 *
 * Protocol semantics, queue behavior, retry/error policy: ADR-0004.
 * Storage semantics: ADR-0005. Both sides are tested against the shared
 * fixtures in tests/fixtures (ADR-0008).
 *
 * All endpoints sit behind Cloudflare Access; the Worker independently
 * validates the Access JWT (Cf-Access-Jwt-Assertion) and the owner identity
 * on every request. An expired Access session may surface to the client as a
 * redirect or an HTML response rather than JSON — clients must fetch with
 * redirect: 'manual' and treat that as reauth_required, never as retryable.
 */

import type { CompassEvent, ExportFile } from './events';
import type { EventId, SyncCursor } from './ids';

// ---------------------------------------------------------------------------
// Routes and limits
// ---------------------------------------------------------------------------

export const API_ROUTES = {
  /** POST */
  push: '/api/sync/push',
  /** GET ?cursor=<number>&limit=<number> */
  pull: '/api/sync/pull',
  /** GET — returns ExportFile. Import has no endpoint (ADR-0004). */
  export: '/api/export',
} as const;

export const MAX_PUSH_BATCH = 500;
export const MAX_PULL_LIMIT = 500;

// ---------------------------------------------------------------------------
// Push (idempotent by construction — replaying any batch is safe)
// ---------------------------------------------------------------------------

export interface PushRequest {
  /** 1..MAX_PUSH_BATCH events. */
  readonly events: readonly CompassEvent[];
}

export interface PushResponse {
  /** Newly stored this call. */
  readonly accepted: readonly EventId[];
  /** Already present — equally final from the client's perspective. */
  readonly duplicates: readonly EventId[];
  /** Latest cursor after the write; lets the client detect it is behind. */
  readonly serverCursor: SyncCursor;
}

// ---------------------------------------------------------------------------
// Pull (since cursor, ordered by server sequence)
// ---------------------------------------------------------------------------

export interface SyncedEvent {
  readonly event: CompassEvent;
  readonly serverSeq: SyncCursor;
}

export interface PullResponse {
  /** Events with serverSeq > request cursor, ascending, up to limit. */
  readonly events: readonly SyncedEvent[];
  /** Echo back as the next request's cursor. */
  readonly nextCursor: SyncCursor;
  readonly hasMore: boolean;
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export type ExportResponse = ExportFile;

// ---------------------------------------------------------------------------
// Errors (JSON body on any non-2xx the Worker itself produces)
// ---------------------------------------------------------------------------

export type ApiErrorCode =
  /** 401 — missing/invalid Access JWT. */
  | 'unauthorized'
  /** 403 — valid JWT, wrong identity (not the owner). */
  | 'forbidden'
  /** 400 — malformed body or envelope (shape-only validation, ADR-0005). */
  | 'invalid_request'
  /** 400 — more than MAX_PUSH_BATCH events. */
  | 'batch_too_large'
  /** 500 — anything else; client retries with backoff. */
  | 'internal';

export interface ApiErrorBody {
  readonly error: {
    readonly code: ApiErrorCode;
    readonly message: string;
  };
}
